import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/app/auth';
import { ApiError, api } from '@/lib/api';
import { GREFFER } from '@/lib/status';
import type { Greffer } from './types';

export function useGreffers() {
  const isAuthenticated = useAuth((s) => s.isAuthenticated);
  return useQuery({
    queryKey: ['greffers'],
    queryFn: async () => (await api.get<{ data: Greffer[] }>('/api/greffer/')).data,
    enabled: isAuthenticated,
    // Poll fast while any greffer is still registering/created so a freshly
    // checked-in greffer flips to Registering/Registered without a refresh.
    // Then keep a slower steady poll while the backend reports heartbeat
    // liveness: 'unreachable'/'degraded'/last_seen flip WHILE a greffer stays
    // REGISTERED, so a transition-gated poll would leave the chip green forever.
    // No liveness field at all (legacy backend) → today's poll-then-stop.
    refetchInterval: (query) => {
      const list = query.state.data ?? [];
      if (list.length === 0) return false;
      if (list.some((g) => g.status !== GREFFER.REGISTERED)) return 8000;
      return list.some((g) => g.liveness != null) ? 15000 : false;
    },
  });
}

export function useCreateGreffer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => api.post<Greffer>('/api/greffer/', { name }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['greffers'] }),
  });
}

/**
 * What an accept_register failure actually means.
 *
 * The endpoint answers with several distinct 409s and a 503, and treating
 * them all as "it failed" was wrong in one specific and user-visible way:
 * `already_accepted` means the machine IS connected — most often because
 * accept was posted twice for a settled greffer (a double-click; the backend
 * pins this in test_reposting_accept_for_a_settled_proxy_is_refused) — so
 * reporting a failure told the user the opposite of the truth and invited a
 * retry that cannot succeed. NOT the tunnel-readiness retry, which bypasses
 * this response and returns 200.
 *
 * Codes are the manager's, see accept_register in
 * backend/apps/greffer/views.py.
 */
export type AcceptFailure =
  /** Already connected. Not a failure: the goal has been reached. */
  | { kind: 'already-connected' }
  /** 402 — at the plan's greffer cap. Rendered inline, not as a toast. */
  | { kind: 'quota' }
  /**
   * Promoted and committed as REGISTERED, but the readiness probe timed out.
   *
   * Neither a success nor a failure, and callers must not record it as
   * either: the row IS registered, so "failed" under-counts a real
   * connection, but the probe never returned 200, so "connected" may be a
   * lie if rathole-client is failing rather than slow. Show the state, claim
   * nothing, until the backend records readiness separately.
   */
  | { kind: 'pending-tunnel'; message: string }
  /** Everything else. */
  | { kind: 'failed'; message: string };

function acceptCode(e: unknown, status: number): string | null {
  // Pinned to the status the backend actually pairs the code with. Matching
  // on the message alone would trust `already_accepted` arriving on a 500, or
  // `tunnel_not_up` on a 409 -- pairings no backend path produces, so a
  // mismatch means something is wrong and the generic message is the honest
  // answer rather than a confident one.
  if (!(e instanceof ApiError) || e.status !== status) return null;
  const body = e.body;
  if (body && typeof body === 'object' && 'message' in body) {
    const m = (body as { message?: unknown }).message;
    if (typeof m === 'string') return m;
  }
  return null;
}

/**
 * `subject` names the machine where the caller has one ("prod-1"), so the
 * list view can say which row failed; the connect flow is already about a
 * single machine and passes nothing.
 */
export function readAcceptFailure(e: unknown, subject?: string): AcceptFailure {
  // Sentence-initial, so it must be capitalised: the connect flow passes no
  // subject and produced "this machine is not waiting…" mid-toast.
  const Who = subject ?? 'This machine';
  const whose = subject ? `${subject}'s` : 'its';
  if (e instanceof ApiError && e.status === 402) return { kind: 'quota' };
  if (e instanceof ApiError && e.status === 404) {
    // Unknown id, not owned, or removed while we held the lock. Whatever the
    // cause the row is not there, so "please try again" -- the generic
    // message -- would invite the same impossible retry this function exists
    // to stop handing out.
    return { kind: 'failed', message: `${Who} is no longer here. Refresh to see what is.` };
  }
  if (acceptCode(e, 503) === 'tunnel_not_up') {
    return {
      kind: 'pending-tunnel',
      message: `${subject ?? 'This machine'} is connected, but its tunnel is still coming up. This usually finishes on its own.`,
    };
  }

  switch (acceptCode(e, 409)) {
    case 'already_accepted':
      return { kind: 'already-connected' };
    case 'not_awaiting_acceptance':
      return { kind: 'failed', message: `${Who} is not waiting to be accepted. Refresh and check its status.` };
    case 'identity_changed':
      return { kind: 'failed', message: `${Who} reconnected with different details while you were confirming. Check it and try again.` };
    case 'address_in_use':
      return { kind: 'failed', message: `Another machine is already using ${whose} address. Give one of them a different address first.` };
    case 'invalid_greffer_address':
      return { kind: 'failed', message: `${Who} reported an address that cannot be secured. Change its address and connect again.` };
    default:
      return { kind: 'failed', message: subject ? `Could not accept ${subject}.` : 'Could not confirm this machine. Please try again.' };
  }
}

/** Accept a registering greffer — quota-gated (402 when at the greffer limit). */
export function useAcceptGreffer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<Greffer>(`/api/greffer/register/accept/${id}/`, {}),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['greffers'] });
      qc.invalidateQueries({ queryKey: ['subscription'] });
    },
  });
}

/**
 * Trigger a manager-driven remote update of a greffer to the latest version
 * (greffer self-update v2). The manager resolves the target server-side, calls
 * the greffer's controller, and returns 202; we invalidate so the list reflects
 * the `updating` grace. The body is empty: the target is never client-chosen.
 */
export function useUpdateGreffer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<Greffer>(`/api/greffer/${id}/update/`, {}),
    onSettled: () => qc.invalidateQueries({ queryKey: ['greffers'] }),
  });
}

export function useDeleteGreffer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      try {
        await api.del<void>(`/api/greffer/${id}/`);
      } catch (e) {
        // 410 AND 404 both mean "it is not there", which is the outcome the
        // caller asked for. Only 410 was swallowed, so removing a machine that
        // had already been deleted elsewhere rejected, `onSuccess` never ran,
        // and Get-started never cleared the id it was holding: the user stayed
        // pinned on a phantom machine's troubleshooting view until a reload.
        if (e instanceof ApiError && (e.status === 410 || e.status === 404)) return;
        throw e;
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['greffers'] });
      qc.invalidateQueries({ queryKey: ['instances'] });
      qc.invalidateQueries({ queryKey: ['subscription'] });
    },
  });
}

/**
 * What a recheck-readiness response actually means.
 *
 * The endpoint answers with TWO distinct 503s that mean opposite things, and
 * conflating them is the specific mistake this type exists to prevent:
 * `tunnel_not_up` is "we probed and your tunnel is down" -- a real fault the
 * user must go fix -- while `recheck_busy` is "we probed nothing because the
 * manager had no free slot", which says nothing about their tunnel at all.
 * Reporting the second as the first tells someone their infrastructure is
 * broken when it may be perfectly healthy.
 *
 * Codes are the manager's, see recheck_readiness in
 * backend/apps/greffer/views.py.
 */
export type RecheckOutcome =
  /** Probed, and the path is proved. */
  | { kind: 'proved' }
  /** Probed, and the tunnel is genuinely down. The actionable failure. */
  | { kind: 'not-up' }
  /**
   * Nothing was probed: the manager's global slot bank was full.
   *
   * Retrying is meaningful and likely to work shortly, so this must NOT be
   * shown as a tunnel fault.
   */
  | { kind: 'busy' }
  /**
   * Nothing was probed, but the row IS in the prober's queue, so a background
   * probe really is coming. "Queued", not an error.
   */
  | { kind: 'deferred' }
  /** Per-greffer hourly limit. Retrying SOON is not meaningful. */
  | { kind: 'rate-limited' }
  /**
   * Probed, but the answer belongs to a readiness episode that has moved on
   * (the greffer re-registered mid-probe). We cannot assert a result.
   */
  | { kind: 'not-checked' }
  | { kind: 'failed'; message: string };

export function readRecheckOutcome(e: unknown): RecheckOutcome {
  // 202 and 200 are successes and never reach here; see useRecheckReadiness.
  if (!(e instanceof ApiError)) {
    return { kind: 'failed', message: 'Could not reach the manager.' };
  }
  if (e.status === 429) return { kind: 'rate-limited' };
  if (e.status === 409) return { kind: 'failed', message: 'This machine has no tunnel.' };
  // 404 = unknown or not-owned, sent with an empty body: deleted in another tab
  // or by an admin while this page was open. The generic fallback below invites
  // a retry of something that can never succeed. readAcceptFailure names this
  // status explicitly for exactly the same reason.
  if (e.status === 404) return { kind: 'failed', message: 'This machine no longer exists.' };
  // Paired with the status the backend actually sends it on, for the same
  // reason acceptCode does: `recheck_busy` arriving on anything but a 503
  // means something is wrong, and the generic answer is the honest one.
  const code = acceptCode(e, 503);
  if (code === 'recheck_busy') return { kind: 'busy' };
  if (code === 'tunnel_not_up') return { kind: 'not-up' };
  if (code === 'readiness_not_checked') return { kind: 'not-checked' };
  return { kind: 'failed', message: 'Could not check this machine.' };
}

/**
 * Ask the manager to re-probe a tunnel greffer's path now.
 *
 * Resolves for every outcome the user can act on rather than throwing, so the
 * caller renders one state machine instead of splitting it across onSuccess
 * and onError. Only a genuine transport failure rejects.
 */
export function useRecheckReadiness() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<RecheckOutcome> => {
      try {
        const r = await api.post<{ message?: string; tunnel_readiness_unproven_since?: string | null }>(
          `/api/greffer/${id}/recheck-readiness/`,
          {},
        );
        // Assert proof POSITIVELY, from the refreshed row the 200 carries.
        //
        // `anything that isn't recheck_deferred` made 'proved' the default for
        // every 2xx, and `apiFetch` hands back only the body, so the client
        // cannot see the status code. An empty 2xx (`r` null), a non-JSON 2xx
        // (an edge or proxy interstitial, which arrives as a string), or any
        // future rewording of the 202 message all became a green "Reached this
        // machine through its tunnel." over a row that is still unproven.
        // Proof is the one outcome that must never be reached by elimination.
        if (r && typeof r === 'object' && r.tunnel_readiness_unproven_since === null) {
          return { kind: 'proved' };
        }
        if (r && typeof r === 'object' && r.message === 'recheck_deferred') return { kind: 'deferred' };
        // A success we do not recognise. Something happened, the caches are
        // invalidated below, and the row itself will say what is true.
        return { kind: 'not-checked' };
      } catch (e) {
        return readRecheckOutcome(e);
      }
    },
    // Refresh either way: a proof clears the chip, and a failed probe may have
    // just OPENED readiness on a row that read healthy a moment ago.
    // BOTH caches. The list reads ['greffers']; the detail page also renders
    // greffons-on-host from ['instances'], whose embedded greffer object
    // carries the same readiness field. Invalidating only one left a proved
    // path rendering its success message underneath a panel still asserting
    // the machine was unreachable, until an unrelated 15s poll landed.
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['greffers'] });
      qc.invalidateQueries({ queryKey: ['instances'] });
    },
  });
}
